// Copyright 2025. WebPros International GmbH. All rights reserved.

import { IComputeResourceResponse } from 'common/api/resources/ComputeResource';
import { initialComputeResourceState } from 'admin/computeResource/reducers';

export interface IComputeResourceRecoveryReadinessStatusResponse {
    available_resources: {
        is_disk_unlimited: boolean;
        is_ram_unlimited: boolean;
        is_vcpu_unlimited: boolean;
        disk: number;
        ram: number;
        vcpu: number;
    };
    backup_coverage: {
        all: IBackupCoverage;
        with_disaster_recovery_enabled: IBackupCoverage;
    };
    destination_compute_resources: Array<{
        compute_resource: IComputeResourceResponse;
        compatibility_details: {
            arch_type_is_compatible: boolean;
            compatible_destination_cr_ip_blocks_count: number;
            destination_cr_capabilities_exists: boolean;
            destination_cr_is_active: boolean;
            destination_cr_os_is_supported: boolean;
            is_compatible: boolean;
            products_is_compatible: boolean;
            source_cr_capabilities_exists: boolean;
            source_cr_ip_blocks_count: number;
            source_cr_os_is_supported: boolean;
            sources_is_compatible: boolean;
            storages_is_compatible: boolean;
            supported_virtualization_types: {
                kvm?: boolean;
                vz?: boolean;
            };
            missed_ip_blocks: Array<{
                id: number;
                name: string;
            }>;
            incompatible_storages: Array<{
                type: string;
                tier: string;
            }>;
        };
    }>;
    require_resources: {
        for_vms_disaster_recovery_enabled: IRequireResources;
        for_vms_with_backups: IRequireResources;
        for_all_vms: IRequireResources;
    };
    ready_for_recovery: boolean;
}

interface IBackupCoverage {
    available_backups: number;
    expired_backups: number;
    total: number;
    without_backups: number;
}

interface IRequireResources {
    vcpu: number;
    ram: number;
    disk: number;
}

export const defaultComputeResourceReadinessStatus: IComputeResourceRecoveryReadinessStatusResponse = {
    available_resources: {
        is_disk_unlimited: true,
        is_ram_unlimited: true,
        is_vcpu_unlimited: true,
        disk: 0,
        ram: 0,
        vcpu: 0,
    },
    backup_coverage: {
        all: {
            available_backups: 1,
            expired_backups: 0,
            total: 1,
            without_backups: 0,
        },
        with_disaster_recovery_enabled: {
            available_backups: 1,
            expired_backups: 0,
            total: 1,
            without_backups: 0,
        },
    },
    destination_compute_resources: [
        {
            compute_resource: {
                ...initialComputeResourceState,
                id: 2,
            },
            compatibility_details: {
                arch_type_is_compatible: true,
                compatible_destination_cr_ip_blocks_count: 1,
                destination_cr_capabilities_exists: true,
                destination_cr_is_active: true,
                destination_cr_os_is_supported: true,
                is_compatible: true,
                products_is_compatible: true,
                source_cr_capabilities_exists: true,
                source_cr_ip_blocks_count: 1,
                source_cr_os_is_supported: true,
                sources_is_compatible: true,
                storages_is_compatible: true,
                supported_virtualization_types: {
                    kvm: true,
                    vz: true,
                },
                missed_ip_blocks: [],
                incompatible_storages: [],
            },
        },
    ],
    require_resources: {
        for_all_vms: {
            vcpu: 2,
            ram: 4294967296,
            disk: 10,
        },
        for_vms_with_backups: {
            vcpu: 2,
            ram: 4294967296,
            disk: 10,
        },
        for_vms_disaster_recovery_enabled: {
            vcpu: 2,
            ram: 4294967296,
            disk: 10,
        },
    },
    ready_for_recovery: true,
};
